package com.kaihuoy.shared.data

import kotlinx.serialization.KSerializer
import kotlinx.serialization.Serializable
import kotlinx.serialization.descriptors.PrimitiveKind
import kotlinx.serialization.descriptors.PrimitiveSerialDescriptor
import kotlinx.serialization.descriptors.SerialDescriptor
import kotlinx.serialization.encoding.Decoder
import kotlinx.serialization.encoding.Encoder


object DoubleAsLongSerializer : KSerializer<Long> {
    override val descriptor: SerialDescriptor = PrimitiveSerialDescriptor("DoubleAsLong", PrimitiveKind.STRING)
    override fun serialize(encoder: Encoder, value: Long) = encoder.encodeDouble(value.toDouble())
    override fun deserialize(decoder: Decoder): Long = try {
        decoder.decodeString().toDoubleOrNull()?.toLong() ?: 0L
    } catch (e: Exception) {
        0L
    }
}

@Serializable
data class InductionRecord(
    val id: String = "",
    val userName: String = "",
    val birthDate: String = "",
    val employer: String = "",
    val taxNumber: String = "",
    val address: String = "",
    val phone: String = "",
    val siteName: String = "",
    val training: Map<String, String> = emptyMap(),
    val writtenPermission: Boolean = false,
    val generalChecked: List<String> = emptyList(),
    val workSiteChecked: List<String> = emptyList(),
    val generalRemarks: Map<String, String> = emptyMap(),
    val workSiteRemarks: Map<String, String> = emptyMap(),
    val introducerName: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    val userId: String = "",
    val status: String = "draft", // "draft" tai "completed"
    val currentStep: Int = 1,
    val flowVersion: Int = 1
)

@Serializable
data class InspectionRecord(
    val id: String = "",
    val userName: String = "",
    val siteName: String = "",
    val siteAddress: String = "",
    val machineType: String = "",
    val machineManufacturerModel: String = "",
    val serialNumber: String = "",
    val manufacturingYear: String = "",
    val operatingHours: String = "",
    val ownerName: String = "",
    val phone: String = "",
    val email: String = "",
    val checklist: Map<String, String> = emptyMap(), // Value: "Kunnossa", "Korjattava", "Huom."
    val repairNotes: String = "",
    val driverName: String = "",
    val driverExperience: String = "",
    val participants: String = "",
    val resolvedChecklistItems: List<String> = emptyList(),
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    val userId: String = ""
)

@Serializable
data class ServiceRecord(
    val id: String = "",
    val userName: String = "",
    val serviceType: String = "", // e.g., "Huolto", "Korjaus", "Tarkastus"
    val machineHours: String = "",
    val equipmentId: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val serviceDate: Long = 0,
    val userId: String = ""
)

@Serializable
data class User(
    val uid: String = "",
    val email: String = "",
    val displayName: String = "",
    val isAdmin: Boolean = false,
    val isHuolto: Boolean = false,
    val isPutkityo: Boolean = false,
    val birthDate: String = "",
    val employer: String = "",
    val taxNumber: String = "",
    val address: String = "",
    val phone: String = ""
)

@Serializable
data class Announcement(
    val id: String = "",
    val title: String = "",
    val message: String = "",
    val createdByName: String = "",
    val userId: String = "",
    val isPinned: Boolean = false,
    val isActive: Boolean = true,
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val expiresAt: Long = 0,
    val isArchived: Boolean = false,
    @Serializable(with = DoubleAsLongSerializer::class)
    val updatedAt: Long = 0
)

@Serializable
data class Machine(
    val id: String = "",
    val machineNumber: String = "",
    val manufacturer: String = "",
    val model: String = "",
    val manufacturingYear: String = "",
    val serialNumber: String = "",
    val status: String = "vapaa", // "käytössä", "vapaa", "työmaalla"
    val worksite: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val currentHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val lastEngineOilChangeHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val lastHydraulicOilChangeHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val lastTransmissionOilChangeHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val lastBrakeOilChangeHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val lastAxleOilChangeHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val lastHubOilChangeHours: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    val userId: String = ""
)

@Serializable
data class InductionTemplate(
    val id: String = "",
    val title: String = "",
    val description: String = "",
    val category: String = "general", // general, site, specialized
    val items: List<String> = emptyList(),
    val questions: List<PohjaKysymys> = emptyList(), // tyyppi: "checkbox", "choice", "heading"
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0
)

@Serializable
data class ScheduledMaintenance(
    val id: String = "",
    val equipmentId: String = "",       // Koneen id (Machine.id)
    val machineNumber: String = "",     // Koneen numero (Machine.machineNumber)
    @Serializable(with = DoubleAsLongSerializer::class)
    val reportedHours: Long = 0,          // Käyttötunnit ilmoitushetkellä
    val title: String = "",             // Huollon otsikko, esim. "Moottoriöljyn vaihto"
    val description: String = "",       // Vapaamuotoinen kuvaus
    val oilChangeTypes: List<String> = emptyList(),
    val serviceType: String = "",       // "Huolto", "Korjaus", "Tarkastus", "Määräaikaishuolto"
    @Serializable(with = DoubleAsLongSerializer::class)
    val scheduledDate: Long = 0,        // Ilmoituspäivä / vanhoissa tiedoissa ajoitettu päivä (millis)
    @Serializable(with = DoubleAsLongSerializer::class)
    val completedDate: Long = 0,        // Suorituspäivä (0 = ei vielä tehty)
    @Serializable(with = DoubleAsLongSerializer::class)
    val completedHours: Long = 0,       // Käyttötunnit huollon valmistuessa
    val intervalHours: Int = 0,         // Huoltoväli tunteina (esim. 500h)
    val status: String = "scheduled",   // "reported", "scheduled", "completed", "overdue"
    val assignedTo: String = "",        // Ilmoittaja / vanhoissa tiedoissa vastuuhenkilö
    val notes: String = "",             // Lisähuomiot
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    val userId: String = ""
)

@Serializable
data class LoadRecord(
    val id: Long = 0,
    val driverUid: String = "",
    val driverName: String = "",
    val materialType: String = "",
    val machineNumber: String = "",
    val jobSite: String = "",
    val volumeWeight: Double? = null,
    val unit: String = "",
    val loadingPlace: String = "",
    val unloadingPlace: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    val status: String = "",
    val sent: Boolean = false,
    val pickupLatitude: Double? = null,
    val pickupLongitude: Double? = null,
    val deliveryLatitude: Double? = null,
    val deliveryLongitude: Double? = null,
    val photoUri: String? = null
)

@Serializable
data class TemplateQuestion(
    val label: String = "",
    val type: String = "checkbox", // "checkbox" tai "choice"
    val options: List<String> = emptyList()
)

@Serializable
data class PerehdytysMateriaali(
    val id: String = "",
    val otsikko: String = "",
    val kuvaus: String = "",
    val tiedostoUrl: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val lisatty: Long = 0,
    val lisaajaId: String = "",
    val kohderyhma: String = "kaikki"
)

@Serializable
data class PohjaKysymys(
    val id: String = "",
    val teksti: String = "",
    val tyyppi: String = "checkbox",
    val vaihtoehdot: List<String> = emptyList()
)

@Serializable
data class PerehdytysPohja(
    val id: String = "",
    val otsikko: String = "",
    val kuvaus: String = "",
    val kohderyhma: String = "kaikki",
    @Serializable(with = DoubleAsLongSerializer::class)
    val lisatty: Long = 0,
    @Serializable(with = DoubleAsLongSerializer::class)
    val muokattu: Long = 0,
    val lisaajaId: String = "",
    val patevyydet: List<String> = emptyList(),
    val kysymykset: List<PohjaKysymys> = emptyList()
)

@Serializable
data class Tyomaa(
    val id: String = "",
    val nimi: String = "",
    val osoite: String = "",
    val lisattyById: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val lisatty: Long = 0
)

@Serializable
data class PaineenNosto(
    val aika: String = "",
    val vedenMaara: String = ""
)

@Serializable
data class PainekoeRecord(
    val id: String = "",
    val userId: String = "",
    val userName: String = "",
    val asennusPvm: String = "",
    val rakennusTiedot: String = "",
    val tilaaja: String = "",
    val puhelin: String = "",
    val katuosoite: String = "",
    val postinumero: String = "",
    val jarjestelmanKuvaus: String = "",
    val lahtopaine: String = "",
    val nostetaanPaine: String = "",
    val vedenMaaraAlussa: String = "",
    val pidetaanPaine: String = "",
    val paineenNostot: List<PaineenNosto> = emptyList(),
    val lasketaanPaine: String = "",
    val vedenMaaraUlos: String = "",
    val loppullinenVedenMaaraUlos: String = "",
    val osio3: String = "",
    val huomautuksia: String = "",
    val kokeenSuorittaja: String = "",
    val tilaajanEdustaja: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val timestamp: Long = 0,
    val status: String = "draft"
)

@Serializable
data class PerehdytysVastaus(
    val id: String = "",
    val pohjaId: String = "",
    val pohjaOtsikko: String = "",
    val userId: String = "",
    val userName: String = "",
    val employer: String = "",
    val taxNumber: String = "",
    val address: String = "",
    val phone: String = "",
    val birthDate: String = "",
    val tyomaa: String = "",
    @Serializable(with = DoubleAsLongSerializer::class)
    val aika: Long = 0,
    val patevyydet: Map<String, Boolean> = emptyMap(),
    val patevyydetPvm: Map<String, String> = emptyMap(),
    val vastaukset: Map<String, String> = emptyMap()
)
